import PropTypes from "prop-types";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

// searchable select — must pick from the list, typing only filters
const MaterialSelect = ({ value, onChange, options, disabled, hasError, compact }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [dropPos, setDropPos] = useState(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // display the selected value's translated name when not searching
  const displayText = isOpen ? search : value ? t(value) : "";

  const filtered = search
    ? options.filter(
        (opt) =>
          opt.name.toLowerCase().includes(search.toLowerCase()) ||
          (opt.material_category || "")
            .toLowerCase()
            .includes(search.toLowerCase())
      )
    : options;

  // calc dropdown position from input bounding rect
  const updatePos = useCallback(() => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setDropPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // update position when open, on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePos();
    const scrollParents = [];
    let el = wrapRef.current;
    while (el) {
      el = el.parentElement;
      if (el) scrollParents.push(el);
    }
    const onReposition = () => updatePos();
    scrollParents.forEach((p) => p.addEventListener("scroll", onReposition, { passive: true }));
    window.addEventListener("resize", onReposition, { passive: true });
    return () => {
      scrollParents.forEach((p) => p.removeEventListener("scroll", onReposition));
      window.removeEventListener("resize", onReposition);
    };
  }, [isOpen, updatePos]);

  const handleInput = (e) => {
    setSearch(e.target.value);
    setIsOpen(true);
    setHighlightIdx(-1);
  };

  const handleSelect = useCallback(
    (name) => {
      onChange(name);
      setSearch("");
      setIsOpen(false);
      setHighlightIdx(-1);
    },
    [onChange]
  );

  const inputRef = useRef(null);
  const wasFocused = useRef(false);

  // capture focus state BEFORE focus event fires
  const handleMouseDown = () => {
    wasFocused.current = document.activeElement === inputRef.current;
  };

  const handleFocus = () => {
    setSearch("");
    setIsOpen(true);
  };

  const handleClick = () => {
    // if input was already focused, this is a re-click — toggle
    if (wasFocused.current) {
      if (isOpen) {
        setIsOpen(false);
        setSearch("");
      } else {
        setSearch("");
        setIsOpen(true);
      }
    }
    // if not already focused, handleFocus already opened it
  };

  const handleKeyDown = (e) => {
    if (!isOpen || !filtered.length) {
      if (e.key === "ArrowDown") {
        setIsOpen(true);
        setSearch("");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIdx].name);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
    }
  };

  // scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIdx];
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIdx]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target) &&
        (!listRef.current || !listRef.current.contains(e.target))
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dropdownList = isOpen && dropPos && (
    <>
      {filtered.length > 0 && (
        <ul
          ref={listRef}
          className="material-combobox-list"
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={opt.name}
              className={`material-combobox-item ${
                i === highlightIdx ? "highlighted" : ""
              } ${opt.name === value ? "selected" : ""}`}
              onMouseDown={() => handleSelect(opt.name)}
              onMouseEnter={() => setHighlightIdx(i)}
            >
              <span className="material-combobox-name">{t(opt.name)}</span>
              {opt.material_category && (
                <span className="material-combobox-cat">
                  {t(opt.material_category)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {search && filtered.length === 0 && (
        <div
          className="material-combobox-list"
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
          }}
        >
          <div className="material-combobox-empty">
            {t("No matching materials")}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div ref={wrapRef} className={`material-combobox ${isOpen ? "open" : ""}`}>
      <input
        ref={inputRef}
        type="text"
        className={`field-input ${hasError ? "error" : ""} ${compact ? "compact" : ""}`}
        value={displayText}
        onChange={handleInput}
        onMouseDown={handleMouseDown}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={compact ? "" : t("Search material type...")}
        autoComplete="off"
      />
      <span className="material-combobox-chevron" />
      {createPortal(dropdownList, document.body)}
    </div>
  );
};

MaterialSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  compact: PropTypes.bool,
};

export default MaterialSelect;
